import { useCallback } from 'react';
import { useNotifications } from '@/contexts/NotificationContext';
import { useMoveWarranty } from './useWarrantyMutations';

/**
 * Logique de réordonnancement partagée entre la vue tableau et la vue grille.
 *
 * Elle était auparavant dupliquée à l'identique dans les deux composants, avec
 * le même défaut : envoyer la valeur `order` de la garantie cible. Comme la
 * liste affichée est filtrée et paginée, cette valeur ne décrit pas la position
 * visée ; et tant que deux garanties partageaient un même ordre, elle ne
 * désignait même pas une ligne unique. On envoie désormais un repère relatif.
 */
export const useWarrantyReorder = (warranties: Array<{ uuid: string }>) => {
  const { showNotification } = useNotifications();
  const moveWarranty = useMoveWarranty();

  /** Réordonne à partir des index de la liste visible (vue tableau). */
  const reorder = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (oldIndex === newIndex) return;

      const moved = warranties[oldIndex];
      const target = warranties[newIndex];
      if (!moved || !target || moved.uuid === target.uuid) return;

      moveWarranty.mutate(
        {
          id: moved.uuid,
          targetUuid: target.uuid,
          // Descendre = passer APRÈS le repère, monter = passer AVANT.
          // Reproduit exactement la sémantique de `arrayMove` de dnd-kit.
          position: newIndex > oldIndex ? 'after' : 'before',
        },
        {
          onError: () =>
            showNotification('error', "Le réordonnancement a échoué, l'ordre précédent a été rétabli"),
        },
      );
    },
    [warranties, moveWarranty, showNotification],
  );

  /** Réordonne à partir des identifiants dnd-kit (vue grille). */
  const reorderByIds = useCallback(
    (activeId: string, overId: string) => {
      const oldIndex = warranties.findIndex((w) => w.uuid === activeId);
      const newIndex = warranties.findIndex((w) => w.uuid === overId);
      if (oldIndex === -1 || newIndex === -1) return;

      reorder(oldIndex, newIndex);
    },
    [warranties, reorder],
  );

  return { reorder, reorderByIds, isReordering: moveWarranty.isLoading };
};
